import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

export default function Leaderboard() {
  const [players, setPlayers] = useState([])
  const [loading, setLoading] = useState(true)
  const [accessState, setAccessState] = useState('checking')
  const [myEntryIds, setMyEntryIds] = useState([])
  const tournamentStarted = new Date() > new Date('2026-06-11T19:00:00Z')

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: entries } = await supabase.from('entries').select('paid').eq('user_id', user.id)
        const hasPaidEntry = entries?.some(e => e.paid)
        setAccessState(hasPaidEntry ? 'granted' : 'unpaid')
        const { data: myEntries } = await supabase.from('entries').select('id').eq('user_id', user.id)
        setMyEntryIds(myEntries?.map(e => e.id) || [])
      } else {
        setAccessState('loggedout')
      }
      const { data } = await supabase.from('leaderboard').select('*')
      setPlayers(data || [])
      setLoading(false)
    }
    load()
  }, [])

  const NavBar = () => (
    <nav className="topnav">
      <a href="/" className="topnav-logo">PREDICTA</a>
      <a href="/world-cup" className="btn btn-secondary" style={{ padding: '7px 16px', fontSize: '13px' }}>Dashboard</a>
    </nav>
  )

  if (loading) return <div><NavBar /><div className="container"><p className="text-muted">Loading...</p></div></div>

  if (accessState === 'loggedout') return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '8px' }}>
          <a href="/login" className="btn btn-secondary" style={{ padding: '7px 16px', fontSize: '13px' }}>Log in</a>
          <a href="/register" className="btn btn-primary" style={{ padding: '7px 16px', fontSize: '13px' }}>Register</a>
        </div>
      </nav>
      <div style={{ textAlign: 'center', padding: '80px 24px 0' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>🏆</div>
        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '36px', marginBottom: '8px' }}>Leaderboard</h1>
        <p style={{ color: 'var(--text-2)', marginBottom: '32px' }}>You need to be logged in to view the leaderboard.</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <a href="/login" className="btn btn-primary">Log in</a>
          <a href="/register" className="btn btn-secondary">Register</a>
        </div>
      </div>
    </div>
  )

  if (accessState === 'unpaid') return (
    <div>
      <NavBar />
      <div style={{ textAlign: 'center', padding: '80px 24px 0' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔒</div>
        <h1 style={{ fontFamily: 'var(--ff-d)', fontSize: '36px', marginBottom: '8px' }}>Leaderboard</h1>
        <p style={{ color: 'var(--text-2)', maxWidth: '400px', margin: '0 auto 32px', lineHeight: '1.7' }}>
          {tournamentStarted
            ? 'Please ensure payment is made to confirm your entry and unlock the leaderboard.'
            : 'The leaderboard is available to confirmed entries once the tournament kicks off on 11 June 2026.'
          }
        </p>
        <a href="/world-cup" className="btn btn-secondary">Back to dashboard</a>
      </div>
    </div>
  )

  const totalEntries = players.length
  const bestRank = myEntryIds.length > 0
    ? Math.min(...myEntryIds.map(id => players.findIndex(p => p.id === id) + 1).filter(r => r > 0))
    : null

  return (
    <div>
      <NavBar />
      <div className="container">
        <div style={{ marginBottom: '28px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '6px' }}>Live standings</div>
          <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '34px', marginBottom: '6px' }}>Leaderboard</h1>
          <p style={{ color: 'var(--text-2)', fontSize: '13px' }}>Updated after each match result is confirmed by the organiser</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '10px', marginBottom: '28px' }}>
          {[[totalEntries, 'Entries'], ['Jun 11', 'Kickoff'], [bestRank ? `#${bestRank}` : '-', 'Your Rank']].map(([v, l]) => (
            <div key={l} className="stat-card">
              <div className="stat-num">{v}</div>
              <div className="stat-label">{l}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[['1st', '#b45309'], ['2nd', '#6b7280'], ['3rd', '#92400e']].map(([p, c]) => (
            <div key={p} style={{ flex: 1, minWidth: '80px', padding: '14px 16px', textAlign: 'center', background: 'var(--card)', borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', borderTop: `2px solid ${c}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              <div style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '20px', color: c, lineHeight: '1' }}>{p} Place</div>
            </div>
          ))}
        </div>

        {players.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '48px' }}>
            <p style={{ color: 'var(--text-2)' }}>No entries yet. Check back once the competition starts.</p>
          </div>
        ) : (
          <div className="card-flat">
            <table>
              <thead>
                <tr>
                  <th style={{ width: '52px' }}>#</th>
                  <th>Entry</th>
                  <th style={{ textAlign: 'right' }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => {
                  const isMyEntry = myEntryIds.includes(p.id)
                  const rankColors = { 0: '#b45309', 1: '#6b7280', 2: '#92400e' }
                  return (
                    <tr key={p.id} style={{ background: isMyEntry ? 'var(--gold-bg)' : 'transparent' }}>
                      <td>
                        <span style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '22px', color: rankColors[i] || 'var(--text-3)' }}>
                          {i + 1}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontWeight: isMyEntry ? '600' : '400' }}>{p.entry_name}</span>
                          {isMyEntry && <span className="badge-gold">You</span>}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={isMyEntry ? 'gold-text' : ''} style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '22px', color: isMyEntry ? undefined : 'var(--text-2)' }}>
                          {p.total_points}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', padding: '16px', borderTop: '1px solid var(--border-sub)' }}>
              Points update after each confirmed result · Top 3 win prizes
            </p>
          </div>
        )}
      </div>
    </div>
  )
}